import { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { toast } from "sonner";

const SKEY = "payroll_ai_session";

// Minimal markdown: **bold** + preserved line breaks (container uses whitespace-pre-wrap).
const renderRich = (text) =>
  String(text || "").split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>
  );

const getSession = () => {
  let s = localStorage.getItem(SKEY);
  if (!s) {
    s = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Date.now());
    localStorage.setItem(SKEY, s);
  }
  return s;
};

const AIName = ({ size = 12 }) => (
  <span className="flex items-center gap-1 text-[11.5px] font-semibold text-[#0071E3]">
    Meranggi Payroll Assistance <VerifiedBadge size={size} />
  </span>
);

const SUGGESTIONS = [
  "Siapa yang paling rajin bulan ini?",
  "Rate per hari tertinggi siapa?",
  "Apakah ada yang pernah half day?",
];

export function PayrollAIChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const session = useRef(getSession());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      api.get("/ai/history", { params: { session_id: session.current } })
        .then((r) => setMessages(r.data.messages || []))
        .catch(() => {});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" });
  }, [messages, busy]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setBusy(true);
    try {
      const r = await api.post("/ai/payroll-chat", { session_id: session.current, message: q });
      setMessages((m) => [...m, { role: "assistant", text: r.data.answer }]);
    } catch (e) {
      toast.error(errMsg(e));
      setMessages((m) => [...m, { role: "assistant", text: "Maaf, AI sedang tidak tersedia. Coba lagi sebentar." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="ai-fab"
        onClick={() => setOpen((v) => !v)}
        aria-label="Meranggi Payroll Assistance"
        className="fixed bottom-5 right-5 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#0071E3] to-[#0058C7] text-white shadow-[0_10px_30px_rgba(0,88,199,0.45)] transition-transform hover:scale-105 active:scale-95"
      >
        {open ? <X className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
      </button>

      {open && (
        <div
          data-testid="ai-panel"
          className="fixed bottom-24 right-5 z-[60] flex h-[70vh] max-h-[560px] w-[min(92vw,390px)] flex-col overflow-hidden rounded-[22px] border border-white/60 bg-white/90 shadow-[0_24px_70px_rgba(0,0,0,0.28)] backdrop-blur-2xl"
        >
          <div className="flex items-center gap-2 bg-gradient-to-r from-[#0071E3] to-[#0058C7] px-4 py-3 text-white">
            <Sparkles className="h-4 w-4" />
            <span className="flex items-center gap-1 text-[15px] font-semibold">
              Meranggi Payroll Assistance <VerifiedBadge size={15} className="[filter:brightness(0)_invert(1)]" />
            </span>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-3.5 py-3.5">
            {messages.length === 0 && !busy && (
              <div className="space-y-3">
                <div className="rounded-2xl bg-[#F2F2F7] px-3.5 py-3 text-[13.5px] text-[#3A3A3C]">
                  <AIName />
                  <p className="mt-1">Halo! Tanya saya seputar payroll &amp; absensi Meranggi — hari kerja, kasbon, rate, dan kehadiran karyawan.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      data-testid={`ai-suggestion-${i}`}
                      onClick={() => send(s)}
                      className="rounded-full border border-[#E1E1E6] bg-white px-3 py-1.5 text-[12px] text-[#0071E3] transition-colors hover:bg-[#F2F7FD]"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} data-testid={`ai-message-${i}`} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13.5px] ${m.role === "user" ? "bg-[#0071E3] text-white" : "bg-[#F2F2F7] text-[#1D1D1F]"}`}>
                  {m.role === "assistant" && <div className="mb-1"><AIName /></div>}
                  <p className="whitespace-pre-wrap leading-relaxed">{renderRich(m.text)}</p>
                </div>
              </div>
            ))}

            {busy && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-2xl bg-[#F2F2F7] px-3.5 py-2.5 text-[13px] text-[#8E8E93]">
                  <Loader2 className="h-4 w-4 animate-spin" /> Mengetik…
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 border-t border-black/[0.06] p-3">
            <input
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Tanya soal payroll…"
              className="h-10 flex-1 rounded-full border border-[#E1E1E6] bg-white px-4 text-[14px] outline-none focus:border-[#0071E3]"
            />
            <button
              data-testid="ai-send"
              onClick={() => send()}
              disabled={busy || !input.trim()}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0071E3] text-white transition-opacity disabled:opacity-40"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default PayrollAIChat;
